import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Play, 
  Edit2, 
  Trash2, 
  RefreshCw, 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  FileText,
  Navigation,
  Search,
  Link2,
  XCircle
} from 'lucide-react';

const APInvoiceSeriesManager = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState({});
  
  // Modal states
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [showReposition, setShowReposition] = useState(false);
  const [showCheckDoc, setShowCheckDoc] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [repositionSeries, setRepositionSeries] = useState(null);
  const [checkDocSeries, setCheckDocSeries] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    tipo: '',
    serie: '',
    start_number: 1,
    active: true
  });
  const [repositionCursor, setRepositionCursor] = useState('');
  const [repositionConfirm, setRepositionConfirm] = useState(false);
  const [repositionWarning, setRepositionWarning] = useState('');
  
  // Check document states
  const [checkDocNumber, setCheckDocNumber] = useState('');
  const [checkDocResult, setCheckDocResult] = useState(null);
  const [checkingDoc, setCheckingDoc] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      const response = await axios.get(`${API}/admin/ap-invoice-series`);
      setSeries(response.data.series || []);
    } catch (error) {
      toast.error('Erro ao carregar séries');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingSeries(null);
    setFormData({
      tipo: '',
      serie: '',
      start_number: 1,
      active: true
    });
    setShowAddEdit(true);
  };

  const handleEdit = (s) => {
    setEditingSeries(s);
    setFormData({
      tipo: s.tipo,
      serie: s.serie,
      start_number: s.start_number,
      active: s.active
    });
    setShowAddEdit(true);
  };

  const handleSave = async () => {
    if (!formData.tipo.trim() || !formData.serie.trim()) {
      toast.error('Tipo e Série são obrigatórios');
      return;
    }
    if (formData.start_number < 1) {
      toast.error('Número inicial deve ser >= 1');
      return;
    }

    setSaving(true);
    try {
      if (editingSeries) {
        // Update existing
        await axios.put(`${API}/admin/ap-invoice-series/${editingSeries._id}`, {
          start_number: formData.start_number,
          active: formData.active
        });
        toast.success('Série actualizada');
      } else {
        // Create new
        await axios.post(`${API}/admin/ap-invoice-series`, {
          tipo: formData.tipo.toUpperCase().trim(),
          serie: formData.serie.trim(),
          start_number: formData.start_number,
          active: formData.active
        });
        toast.success('Série adicionada');
      }
      setShowAddEdit(false);
      fetchSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await axios.put(`${API}/admin/ap-invoice-series/${s._id}`, {
        active: !s.active
      });
      fetchSeries();
    } catch (error) {
      toast.error('Erro ao alterar estado');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Remover a série ${s.tipo}/${s.serie} da monitorização?`)) return;
    
    setDeleting(s._id);
    try {
      await axios.delete(`${API}/admin/ap-invoice-series/${s._id}`);
      toast.success('Série removida');
      fetchSeries();
    } catch (error) {
      toast.error('Erro ao remover');
    } finally {
      setDeleting(null);
    }
  };

  const handleScan = async (s) => {
    setScanning(prev => ({ ...prev, [s._id]: true }));
    try {
      const response = await axios.post(`${API}/admin/ap-invoice-series/${s._id}/scan`);
      const result = response.data;
      if (result.success === false) {
        toast.error(result.message || 'Scan falhou');
      } else if (result.background) {
        // Background scan started
        toast.success(result.message || `Scan iniciado em background. Atualize a página em alguns segundos.`);
        // Auto-refresh after 5 seconds
        setTimeout(() => {
          fetchSeries();
        }, 5000);
      } else {
        const sr = result.series_results?.[0];
        if (sr) {
          toast.success(
            `Scan ${s.tipo}/${s.serie}: ${sr.documents_found} docs, ${sr.aps_matched} APs ligados`
          );
        } else {
          toast.success('Scan concluído');
        }
      }
      fetchSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no scan');
    } finally {
      setScanning(prev => ({ ...prev, [s._id]: false }));
    }
  };

  const handleOpenReposition = (s) => {
    setRepositionSeries(s);
    setRepositionCursor(s.last_valid_number?.toString() || '');
    setRepositionConfirm(false);
    setRepositionWarning('');
    setShowReposition(true);
  };

  const handleReposition = async () => {
    if (!repositionCursor || parseInt(repositionCursor) < 1) {
      toast.error('Introduza um número válido');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/ap-invoice-series/${repositionSeries._id}/reposition-cursor`,
        {
          new_cursor: parseInt(repositionCursor),
          confirm_skip: repositionConfirm
        }
      );

      if (response.data.requires_confirmation) {
        setRepositionWarning(response.data.warning);
        setRepositionConfirm(false);
      } else if (response.data.success) {
        toast.success('Cursor reposicionado');
        setShowReposition(false);
        fetchSeries();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reposicionar');
    } finally {
      setSaving(false);
    }
  };

  // Check Document Functions
  const handleOpenCheckDoc = (s) => {
    setCheckDocSeries(s);
    setCheckDocNumber('');
    setCheckDocResult(null);
    setShowCheckDoc(true);
  };

  const handleCheckDocument = async () => {
    if (!checkDocNumber || parseInt(checkDocNumber) < 1) {
      toast.error('Introduza um número de documento válido');
      return;
    }

    setCheckingDoc(true);
    setCheckDocResult(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/ap-invoice-series/${checkDocSeries._id}/check-document`,
        { doc_number: parseInt(checkDocNumber) }
      );
      
      setCheckDocResult(response.data);
      
      if (response.data.ap_linked) {
        toast.success(`Fatura ligada ao ${response.data.ap_number}!`);
        fetchSeries();
      } else if (response.data.document) {
        toast.info(response.data.message);
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao verificar documento');
      setCheckDocResult({ success: false, message: error.response?.data?.detail || 'Erro' });
    } finally {
      setCheckingDoc(false);
    }
  };

  const getStatusBadge = (s) => {
    if (!s.status || s.status === 'pending') {
      return <Badge variant="outline" className="text-slate-500 border-slate-300">Pendente</Badge>;
    }
    if (s.status === 'scanning') {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">A processar...</Badge>;
    }
    if (s.status === 'ok') {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">OK</Badge>;
    }
    if (s.status === 'warning') {
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Aviso</Badge>;
    }
    if (s.status === 'error') {
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Erro</Badge>;
    }
    return <Badge variant="outline">{s.status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="ap-invoice-series-card">
        <CardHeader className="p-4 sm:p-6 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileText className="h-5 w-5 text-blue-600" />
              Sincronização de Faturas AP
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSeries}
                data-testid="refresh-series-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={handleAddNew}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-series-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Configure as séries de documentos SAGE a monitorizar para ligar faturas aos APs.
          </p>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-2">
          {series.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="empty-series-state">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhuma série configurada</p>
              <p className="text-sm mt-1">Adicione as séries de documentos que pretende monitorizar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="series-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="pb-2 font-medium text-slate-600 w-16">Ativo</th>
                    <th className="pb-2 font-medium text-slate-600">Tipo</th>
                    <th className="pb-2 font-medium text-slate-600">Série</th>
                    <th className="pb-2 font-medium text-slate-600 text-right">Nº Inicial</th>
                    <th className="pb-2 font-medium text-slate-600 text-right">Último Nº</th>
                    <th className="pb-2 font-medium text-slate-600 text-center">Estado</th>
                    <th className="pb-2 font-medium text-slate-600">Últ. Verificação</th>
                    <th className="pb-2 font-medium text-slate-600 text-right">Acções</th>
                  </tr>
                </thead>
                <tbody>
                  {series.map((s) => (
                    <tr 
                      key={s._id} 
                      className="border-b border-slate-100 hover:bg-slate-50/50"
                      data-testid={`series-row-${s.tipo}-${s.serie}`}
                    >
                      <td className="py-3">
                        <Switch
                          checked={s.active}
                          onCheckedChange={() => handleToggleActive(s)}
                          data-testid={`toggle-active-${s._id}`}
                        />
                      </td>
                      <td className="py-3 font-mono font-medium">{s.tipo}</td>
                      <td className="py-3 font-mono">{s.serie}</td>
                      <td className="py-3 text-right font-mono">{s.start_number?.toLocaleString()}</td>
                      <td className="py-3 text-right font-mono">
                        {s.last_valid_number != null ? s.last_valid_number.toLocaleString() : '-'}
                      </td>
                      <td className="py-3 text-center">{getStatusBadge(s)}</td>
                      <td className="py-3 text-xs text-slate-500">
                        {formatDate(s.last_scan_at)}
                      </td>
                      <td className="py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleOpenCheckDoc(s)}
                            disabled={!s.active}
                            title="Verificar Documento"
                            data-testid={`check-doc-btn-${s._id}`}
                          >
                            <Search className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                            onClick={() => handleEdit(s)}
                            title="Editar"
                            data-testid={`edit-btn-${s._id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => handleOpenReposition(s)}
                            title="Reposicionar Cursor"
                            data-testid={`reposition-btn-${s._id}`}
                          >
                            <Navigation className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(s)}
                            disabled={deleting === s._id}
                            title="Remover"
                            data-testid={`delete-btn-${s._id}`}
                          >
                            {deleting === s._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Stats summary */}
          {series.length > 0 && (
            <div className="flex gap-4 mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500">
              <span>
                <strong className="text-slate-700">{series.filter(s => s.active).length}</strong> activas
              </span>
              <span>
                <strong className="text-slate-700">{series.reduce((sum, s) => sum + (s.documents_found || 0), 0).toLocaleString()}</strong> documentos encontrados
              </span>
              <span>
                <strong className="text-slate-700">{series.reduce((sum, s) => sum + (s.aps_matched || 0), 0).toLocaleString()}</strong> APs ligados
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddEdit} onOpenChange={setShowAddEdit}>
        <DialogContent className="sm:max-w-md" data-testid="add-edit-series-modal">
          <DialogHeader>
            <DialogTitle>
              {editingSeries ? 'Editar Série' : 'Adicionar Série'}
            </DialogTitle>
            <DialogDescription>
              {editingSeries 
                ? 'Altere o número inicial ou estado da série.'
                : 'Configure uma nova série de documentos SAGE para monitorizar.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo de Documento</Label>
                <Input
                  id="tipo"
                  placeholder="FRM"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value.toUpperCase() })}
                  disabled={!!editingSeries}
                  className={editingSeries ? 'bg-slate-100' : ''}
                  data-testid="input-tipo"
                />
                <p className="text-xs text-slate-400">Ex: FRM, FRN, FRT, FT, FR</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="serie">Série</Label>
                <Input
                  id="serie"
                  placeholder="3M"
                  value={formData.serie}
                  onChange={(e) => setFormData({ ...formData, serie: e.target.value })}
                  disabled={!!editingSeries}
                  className={editingSeries ? 'bg-slate-100' : ''}
                  data-testid="input-serie"
                />
                <p className="text-xs text-slate-400">Ex: 3M, 3N, 1, 2</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="start_number">Número Inicial</Label>
              <Input
                id="start_number"
                type="number"
                min="1"
                value={formData.start_number}
                onChange={(e) => setFormData({ ...formData, start_number: parseInt(e.target.value) || 1 })}
                data-testid="input-start-number"
              />
              <p className="text-xs text-slate-400">
                O scanner começa a verificar a partir deste número.
                {editingSeries && editingSeries.last_valid_number && (
                  <span className="text-amber-600">
                    {' '}Cursor actual: {editingSeries.last_valid_number}
                  </span>
                )}
              </p>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Série Activa</p>
                <p className="text-xs text-slate-500">Séries inactivas não são verificadas</p>
              </div>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                data-testid="input-active"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEdit(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-series-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingSeries ? 'Guardar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reposition Cursor Modal */}
      <Dialog open={showReposition} onOpenChange={setShowReposition}>
        <DialogContent className="sm:max-w-md" data-testid="reposition-cursor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="h-5 w-5 text-amber-600" />
              Reposicionar Cursor
            </DialogTitle>
            <DialogDescription>
              {repositionSeries && (
                <>
                  Série: <strong>{repositionSeries.tipo}/{repositionSeries.serie}</strong>
                  <br />
                  Cursor actual: <strong>{repositionSeries.last_valid_number ?? 'Não definido'}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              <strong>Atenção:</strong> Esta acção é para situações excepcionais. 
              Alterar o cursor pode fazer com que documentos não sejam processados.
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_cursor">Novo Cursor (Último Nº Válido)</Label>
              <Input
                id="new_cursor"
                type="number"
                min="1"
                value={repositionCursor}
                onChange={(e) => {
                  setRepositionCursor(e.target.value);
                  setRepositionWarning('');
                  setRepositionConfirm(false);
                }}
                data-testid="input-new-cursor"
              />
              <p className="text-xs text-slate-400">
                O scanner continuará a partir deste número na próxima execução.
              </p>
            </div>
            
            {repositionWarning && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                {repositionWarning}
                <div className="mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={repositionConfirm}
                      onChange={(e) => setRepositionConfirm(e.target.checked)}
                      className="rounded border-red-300"
                      data-testid="confirm-reposition-checkbox"
                    />
                    <span className="font-medium">Confirmo que pretendo reposicionar o cursor</span>
                  </label>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReposition(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleReposition} 
              disabled={saving || (repositionWarning && !repositionConfirm)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reposition-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {repositionWarning ? 'Confirmar Reposição' : 'Reposicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check Document Modal */}
      <Dialog open={showCheckDoc} onOpenChange={setShowCheckDoc}>
        <DialogContent className="sm:max-w-lg" data-testid="check-document-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-green-600" />
              Verificar Documento
            </DialogTitle>
            <DialogDescription>
              {checkDocSeries && (
                <>
                  Série: <strong>{checkDocSeries.tipo}/{checkDocSeries.serie}</strong>
                  <br />
                  Introduza o número do documento SAGE para verificar e ligar a um AP.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="doc_number">Número do Documento</Label>
                <Input
                  id="doc_number"
                  type="number"
                  min="1"
                  placeholder="Ex: 5266"
                  value={checkDocNumber}
                  onChange={(e) => {
                    setCheckDocNumber(e.target.value);
                    setCheckDocResult(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !checkingDoc) {
                      handleCheckDocument();
                    }
                  }}
                  data-testid="input-doc-number"
                />
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={handleCheckDocument} 
                  disabled={checkingDoc || !checkDocNumber}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="verify-document-btn"
                >
                  {checkingDoc ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-2">Verificar</span>
                </Button>
              </div>
            </div>
            
            {/* Result Display - 10 Diagnostic Points */}
            {checkDocResult && (
              <div className="space-y-3">
                {/* Summary message */}
                <div className={`p-3 rounded-lg border ${
                  checkDocResult.diagnostic?.["9_ligacao_efetuada"]
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : checkDocResult.diagnostic?.["1_documento_encontrado_sage"]
                      ? 'bg-blue-50 border-blue-200 text-blue-800' 
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}>
                  <div className="flex items-center gap-2 font-medium">
                    {checkDocResult.diagnostic?.["9_ligacao_efetuada"] ? (
                      <><Link2 className="h-5 w-5" /> {checkDocResult.message}</>
                    ) : checkDocResult.diagnostic?.["1_documento_encontrado_sage"] ? (
                      <><FileText className="h-5 w-5" /> {checkDocResult.message}</>
                    ) : (
                      <><XCircle className="h-5 w-5" /> {checkDocResult.message}</>
                    )}
                  </div>
                </div>
                
                {/* Diagnostic Table */}
                {checkDocResult.diagnostic && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-slate-100">
                        <tr className={checkDocResult.diagnostic["1_documento_encontrado_sage"] ? 'bg-green-50' : 'bg-red-50'}>
                          <td className="px-3 py-2 font-medium text-slate-600">1. Documento encontrado no SAGE</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {checkDocResult.diagnostic["1_documento_encontrado_sage"] 
                              ? <span className="text-green-600 font-bold">✓ Sim</span> 
                              : <span className="text-red-600 font-bold">✗ Não</span>}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 font-medium text-slate-600">2. Tipo</td>
                          <td className="px-3 py-2 text-right font-mono">{checkDocResult.diagnostic["2_tipo"]}</td>
                        </tr>
                        <tr className="bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-600">3. Série</td>
                          <td className="px-3 py-2 text-right font-mono">{checkDocResult.diagnostic["3_serie"]}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 font-medium text-slate-600">4. Número</td>
                          <td className="px-3 py-2 text-right font-mono">{checkDocResult.diagnostic["4_numero"]}</td>
                        </tr>
                        <tr className="bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-600">5. Data</td>
                          <td className="px-3 py-2 text-right font-mono">{checkDocResult.diagnostic["5_data"] || '-'}</td>
                        </tr>
                        <tr className={checkDocResult.diagnostic["6_vosso_numero_sage"] ? '' : 'bg-amber-50'}>
                          <td className="px-3 py-2 font-medium text-slate-600">6. VossoNumero (SAGE)</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {checkDocResult.diagnostic["6_vosso_numero_sage"] 
                              ? <span className="font-bold">{checkDocResult.diagnostic["6_vosso_numero_sage"]}</span>
                              : <span className="text-amber-600">(vazio)</span>}
                          </td>
                        </tr>
                        <tr className="bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-600">7. AP interpretado</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {checkDocResult.diagnostic["7_ap_interpretado"] 
                              ? <span className="font-bold text-blue-600">{checkDocResult.diagnostic["7_ap_interpretado"]}</span>
                              : <span className="text-slate-400">-</span>}
                          </td>
                        </tr>
                        <tr className={checkDocResult.diagnostic["8_ap_encontrado_stockflow"] ? 'bg-green-50' : (checkDocResult.diagnostic["7_ap_interpretado"] ? 'bg-red-50' : '')}>
                          <td className="px-3 py-2 font-medium text-slate-600">8. AP encontrado na StockFlow</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {checkDocResult.diagnostic["8_ap_encontrado_stockflow"] 
                              ? <span className="text-green-600 font-bold">✓ Sim</span>
                              : checkDocResult.diagnostic["7_ap_interpretado"]
                                ? <span className="text-red-600 font-bold">✗ Não</span>
                                : <span className="text-slate-400">-</span>}
                          </td>
                        </tr>
                        <tr className={checkDocResult.diagnostic["9_ligacao_efetuada"] ? 'bg-green-100' : 'bg-slate-50'}>
                          <td className="px-3 py-2 font-medium text-slate-600">9. Ligação efetuada</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {checkDocResult.diagnostic["9_ligacao_efetuada"] 
                              ? <span className="text-green-700 font-bold">✓ Sim</span>
                              : <span className="text-slate-400">Não</span>}
                          </td>
                        </tr>
                        <tr className={checkDocResult.diagnostic["10_sage_invoice_ref_gravado"] ? 'bg-green-50' : ''}>
                          <td className="px-3 py-2 font-medium text-slate-600">10. sage_invoice_ref gravado</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {checkDocResult.diagnostic["10_sage_invoice_ref_gravado"] 
                              ? <span className="text-green-600 font-bold">{checkDocResult.diagnostic["10_sage_invoice_ref_gravado"]}</span>
                              : <span className="text-slate-400">-</span>}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            
            <p className="text-xs text-slate-400">
              Ferramenta de diagnóstico manual. O scanner automático usa a mesma lógica de verificação.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckDoc(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default APInvoiceSeriesManager;
